/**
 * WebSocket 事件收集器
 *
 * 负责消息交互轮次的聚合上报和流式健康度监控。
 * - MessageRoundTracker：跟踪单轮消息生命周期（send → ack → firstToken → complete）
 * - RecentMessageBuffer：环形缓冲区维护最近 N 条消息摘要，异常时作为上下文快照
 * - 包体安全阀：上报前检查序列化大小，超 32KB 按优先级裁剪
 */
import { createShortRandomId } from '@/utils/randomId';

import type {
  ChatPendingDropReason,
  ChatSendContextDiagnostics,
  ChatSendContextPhase,
  ChatSendSource,
} from './wsMonitor';
import type { WsFailReason, WsOperation } from './wsMonitor';
import {
  reportChatAbort,
  reportChatPendingDrop,
  reportChatQueued,
  reportChatReceiveTerminal,
  reportChatSend,
  reportChatSendContext,
  reportChatSendFail,
  reportChatSendSafetyReset,
  reportChatUploadFail,
  reportWsConnect,
  reportWsDisconnect,
  reportWsErrorConnect,
  reportWsErrorServer,
  reportWsHeartbeat,
  reportWsMessageRound,
  reportWsOpFail,
  reportWsOrphanRound,
  reportWsPongTimeout,
  reportWsReconnect,
  reportWsReconnectExhausted,
} from './wsMonitor';

// ============== 常量 ==============

/** 用户消息内容截取上限（字符） */
const USER_CONTENT_MAX_LENGTH = 2000;

/** Agent 回复内容截取上限（字符） */
const AGENT_CONTENT_MAX_LENGTH = 10000;

/** 消息轮次超时（ms），超时后强制聚合上报 */
const MESSAGE_ROUND_TIMEOUT_MS = 120_000;

/** 环形缓冲区容量 */
const RECENT_MESSAGE_BUFFER_SIZE = 5;

/** 内容预览截取长度（用于环形缓冲区摘要） */
const CONTENT_PREVIEW_LENGTH = 100;

/** 包体大小安全阈值（字节） */
const PAYLOAD_SAFE_LIMIT_BYTES = 32_768;

// ============== 类型定义 ==============

/** 消息轮次跟踪器 */
interface MessageRoundTracker {
  msgId: string;
  sendTime: number;
  ackTime?: number;
  firstTokenTime?: number;
  completeTime?: number;
  userContent?: string;
  agentContent?: string;
  agentResponseSize: number;
  isError: boolean;
  errorDetail?: string;
  timeoutTimer: ReturnType<typeof setTimeout>;

  /** 流式健康度 */
  messageDataCount: number;
  lastContentLength: number;
  contentRegressionCount: number;
  contentRegressions: Array<{ chunk: number; from: number; to: number }>;
  maxContentLength: number;
  finalContentLength: number;
}

/** 最近消息摘要（环形缓冲区条目） */
interface RecentMessageSnapshot {
  msgId: string;
  role: string;
  contentPreview: string;
  timestamp: number;
  isError: boolean;
}

/** 聚合上报数据 */
export interface MessageRoundReport {
  msgId: string;
  connectionId: string;
  conversationId: string;
  sessionKey: string;
  isSuccess: boolean;
  ackTimeMs?: number;
  firstTokenTimeMs?: number;
  completeTimeMs?: number;
  agentResponseSize: number;
  messageDataCount: number;
  userContent?: string;
  agentContent?: string;
  errorDetail?: string;
  streamAnomalyFlag: string;
  streamDiagnostics?: string;
  recentMessagesSnapshot?: string;
}

// ============== 流式异常标记构建 ==============

function buildStreamAnomalyFlag(tracker: MessageRoundTracker): string {
  const flags: string[] = [];
  if (!tracker.completeTime) flags.push('timeout');
  if (tracker.contentRegressionCount > 0) flags.push('content_regression');
  if (tracker.messageDataCount > 0 && tracker.finalContentLength === 0) {
    flags.push('empty_response');
  }
  return flags.length > 0 ? flags.join('+') : 'normal';
}

function buildStreamDiagnostics(tracker: MessageRoundTracker): string | undefined {
  if (
    tracker.contentRegressionCount === 0 &&
    tracker.completeTime &&
    tracker.finalContentLength > 0
  ) {
    return undefined;
  }

  return JSON.stringify({
    streamDiag: {
      totalChunks: tracker.messageDataCount,
      maxLen: tracker.maxContentLength,
      finalLen: tracker.finalContentLength,
      regressions: tracker.contentRegressions.slice(0, 10),
    },
  });
}

// ============== 包体安全阀 ==============

function estimateByteSize(value: string): number {
  return new Blob([value]).size;
}

/**
 * 确保聚合上报包体不超过安全阈值。
 * 裁剪优先级：agentContent > userContent > recentMessagesSnapshot
 */
function enforcePayloadLimit(report: MessageRoundReport): MessageRoundReport {
  const serialized = JSON.stringify(report);
  let currentSize = estimateByteSize(serialized);

  if (currentSize <= PAYLOAD_SAFE_LIMIT_BYTES) {
    return report;
  }

  const trimmed = { ...report };

  if (trimmed.agentContent && currentSize > PAYLOAD_SAFE_LIMIT_BYTES) {
    const ratio = PAYLOAD_SAFE_LIMIT_BYTES / currentSize;
    const newLength = Math.floor(trimmed.agentContent.length * ratio * 0.8);
    trimmed.agentContent = trimmed.agentContent.slice(0, Math.max(newLength, 200)) + '...[trimmed]';
    currentSize = estimateByteSize(JSON.stringify(trimmed));
  }

  if (trimmed.userContent && currentSize > PAYLOAD_SAFE_LIMIT_BYTES) {
    trimmed.userContent = trimmed.userContent.slice(0, 500) + '...[trimmed]';
    currentSize = estimateByteSize(JSON.stringify(trimmed));
  }

  if (trimmed.recentMessagesSnapshot && currentSize > PAYLOAD_SAFE_LIMIT_BYTES) {
    trimmed.recentMessagesSnapshot = undefined;
  }

  return trimmed;
}

// ============== 环形缓冲区 ==============

class RecentMessageBuffer {
  private buffer: RecentMessageSnapshot[] = [];
  private maxSize: number;

  constructor(maxSize: number = RECENT_MESSAGE_BUFFER_SIZE) {
    this.maxSize = maxSize;
  }

  push(snapshot: RecentMessageSnapshot): void {
    if (this.buffer.length >= this.maxSize) {
      this.buffer.shift();
    }
    this.buffer.push(snapshot);
  }

  serialize(): string {
    return JSON.stringify(this.buffer);
  }

  clear(): void {
    this.buffer = [];
  }
}

// ============== 事件收集器 ==============

/**
 * 暂存的消息轮次上下文。
 *
 * 服务端 ID 体系：
 *   - ACK.id = 用户消息的服务端 msg_id（持久化标识）
 *   - message_data.msg_id = Agent 运行 ID（runId，前端无法提前预知）
 *
 * 因此 sendMessage 时无法创建 tracker（不知道 Agent 会用什么 msg_id），
 * 需要先暂存 sendTime / ackTime / userContent，
 * 等收到首条 message_data 时才用真实 msg_id 创建 tracker 并回填。
 */
interface PendingRound {
  sendTime: number;
  ackTime?: number;
  userContent: string;
}

export class WsReportCollector {
  private trackers = new Map<string, MessageRoundTracker>();
  private recentMessages = new RecentMessageBuffer();
  private connectionId: string;
  private conversationId = '';
  private sessionKey = '';

  /** 暂存当前轮次的 send/ack 时间，等首条 message_data 到达时回填 */
  private pendingRound: PendingRound | null = null;

  /** 已 finalize 的 msgId，防止 stale message_data（如 final 后迟到的 segment_end）消费 pendingRound */
  private finalizedMsgIds = new Set<string>();

  constructor() {
    this.connectionId = createShortRandomId();
  }

  // ---------- 连接上下文设置 ----------

  /** 连接建立时调用，更新会话标识 */
  setConnectionContext(
    conversationId: string,
    sessionKey: string,
    serverConnectionId?: string,
  ): void {
    this.conversationId = conversationId;
    this.sessionKey = sessionKey;
    if (serverConnectionId) {
      this.connectionId = serverConnectionId;
    }
  }

  /** 连接断开时调用，清理所有进行中的 tracker 和暂存轮次，并补报孤儿数据 */
  handleDisconnect(): void {
    // 补报孤儿 pendingRound：用户已发送但尚未收到任何 message_data
    if (this.pendingRound) {
      const elapsed = Date.now() - this.pendingRound.sendTime;
      reportWsOrphanRound(
        this.connectionId,
        this.conversationId,
        this.sessionKey,
        'pending_round',
        JSON.stringify({
          sendTime: this.pendingRound.sendTime,
          hasAck: !!this.pendingRound.ackTime,
          elapsedMs: elapsed,
          userContentPreview: this.pendingRound.userContent?.slice(0, 100),
        }),
      );
      this.pendingRound = null;
    }

    // 补报孤儿 tracker：已收到部分 message_data 但未收到终态
    for (const [msgId, tracker] of this.trackers) {
      clearTimeout(tracker.timeoutTimer);
      const elapsed = Date.now() - tracker.sendTime;
      reportWsOrphanRound(
        this.connectionId,
        this.conversationId,
        this.sessionKey,
        'tracker',
        JSON.stringify({
          msgId,
          elapsedMs: elapsed,
          hasAck: !!tracker.ackTime,
          hasFirstToken: !!tracker.firstTokenTime,
          messageDataCount: tracker.messageDataCount,
          lastContentLength: tracker.lastContentLength,
          contentRegressionCount: tracker.contentRegressionCount,
        }),
      );
      this.trackers.delete(msgId);
    }

    this.finalizedMsgIds.clear();
  }

  /** 连接重置时重新生成 connectionId */
  resetConnectionId(): void {
    this.connectionId = createShortRandomId();
  }

  getConnectionId(): string {
    return this.connectionId;
  }

  getConversationId(): string {
    return this.conversationId;
  }

  getSessionKey(): string {
    return this.sessionKey;
  }

  // ---------- 便捷上报：WS 连接类 ----------
  // 内部自动取 connectionId / conversationId / sessionKey，调用方无需手动构造 ctx。

  reportConnect(connectTimeMs: number): void {
    reportWsConnect(this.connectionId, this.conversationId, this.sessionKey, connectTimeMs);
  }

  reportDisconnect(
    code: number,
    reason: string,
    aliveDurationMs?: number,
    visibilityState?: string,
    lastPongAgeMs?: number,
  ): void {
    reportWsDisconnect(
      this.connectionId,
      this.conversationId,
      this.sessionKey,
      code,
      reason,
      aliveDurationMs,
      visibilityState,
      lastPongAgeMs,
    );
  }

  reportReconnect(attempt: number): void {
    reportWsReconnect(this.connectionId, this.conversationId, attempt);
  }

  reportReconnectExhausted(maxAttempts: number): void {
    reportWsReconnectExhausted(this.connectionId, this.conversationId, maxAttempts);
  }

  reportHeartbeat(rttMs: number): void {
    reportWsHeartbeat(this.connectionId, this.conversationId, rttMs);
  }

  reportPongTimeout(visibilityState?: string): void {
    reportWsPongTimeout(this.connectionId, this.conversationId, visibilityState);
  }

  reportErrorConnect(errorMsg: string): void {
    reportWsErrorConnect(this.connectionId, this.conversationId, errorMsg);
  }

  reportErrorServer(errorMsg: string, errorCode?: string): void {
    reportWsErrorServer(this.connectionId, this.conversationId, errorMsg, errorCode);
  }

  reportOpFail(
    operation: WsOperation,
    reason: WsFailReason,
    errorDetail?: string,
    userContentPreview?: string,
  ): void {
    reportWsOpFail(
      this.connectionId,
      this.conversationId,
      operation,
      reason,
      errorDetail,
      userContentPreview,
    );
  }

  // ---------- 便捷上报：Chat 发送类 ----------
  // conversationId 默认使用 collector 内部值；部分场景（如 useChatMessaging 中
  // 使用 sendingSessionId）需要 override，通过可选参数 overrideConversationId 支持。

  private buildChatCtx(overrideConversationId?: string) {
    return {
      connectionId: this.connectionId,
      conversationId: overrideConversationId ?? this.conversationId,
      sessionKey: this.sessionKey,
    };
  }

  reportChatSend(
    durationMs: number,
    source: ChatSendSource,
    attachmentCount: number,
    uploadDurationMs?: number,
    userContent?: string,
    overrideConversationId?: string,
  ): void {
    reportChatSend(
      this.buildChatCtx(overrideConversationId),
      durationMs,
      source,
      attachmentCount,
      uploadDurationMs,
      userContent,
    );
  }

  reportChatSendFail(
    errorCode: string,
    phase: 'ws_connect' | 'upload' | 'ws_send' | 'server_reject',
    durationMs: number,
    source: ChatSendSource,
    errorDetail?: string,
    userContent?: string,
    overrideConversationId?: string,
  ): void {
    reportChatSendFail(
      this.buildChatCtx(overrideConversationId),
      errorCode,
      phase,
      durationMs,
      source,
      errorDetail,
      userContent,
    );
  }

  reportChatSendContext(
    phase: ChatSendContextPhase,
    source: ChatSendSource,
    diagnostics: ChatSendContextDiagnostics,
    overrideConversationId?: string,
  ): void {
    reportChatSendContext(
      this.buildChatCtx(overrideConversationId ?? diagnostics.targetConversationId),
      phase,
      source,
      diagnostics,
    );
  }

  reportChatPendingDrop(
    reason: ChatPendingDropReason,
    source: ChatSendSource,
    diagnostics: {
      targetConversationId?: string;
      targetProjectKey?: string;
      activeConversationId?: string;
      pendingId?: string;
      inFlight?: boolean;
    },
    userContent?: string,
    overrideConversationId?: string,
  ): void {
    reportChatPendingDrop(
      this.buildChatCtx(overrideConversationId ?? diagnostics.targetConversationId),
      reason,
      source,
      diagnostics,
      userContent,
    );
  }

  reportChatQueued(
    attachmentCount: number,
    userContent?: string,
    overrideConversationId?: string,
  ): void {
    reportChatQueued(this.buildChatCtx(overrideConversationId), attachmentCount, userContent);
  }

  reportChatUploadFail(
    errorDetail: string,
    fileCount: number,
    overrideConversationId?: string,
  ): void {
    reportChatUploadFail(this.buildChatCtx(overrideConversationId), errorDetail, fileCount);
  }

  reportChatAbort(runId: string, elapsedSinceSendMs?: number): void {
    reportChatAbort(this.buildChatCtx(), runId, elapsedSinceSendMs);
  }

  reportChatSafetyReset(overrideConversationId?: string): void {
    reportChatSendSafetyReset(this.buildChatCtx(overrideConversationId));
  }

  reportChatReceiveTerminal(state: string, messageId: string): void {
    reportChatReceiveTerminal(
      this.connectionId,
      this.conversationId,
      this.sessionKey,
      state,
      messageId,
    );
  }

  // ---------- 消息轮次生命周期 ----------

  /**
   * 用户发送消息时暂存轮次上下文。
   *
   * 此时不创建 tracker，因为还不知道 Agent 回复会用什么 msg_id。
   * 暂存 sendTime 和 userContent，等首条 message_data 到达时回填。
   */
  markMessageSent(userContent: string): void {
    this.pendingRound = {
      sendTime: Date.now(),
      userContent: userContent.slice(0, USER_CONTENT_MAX_LENGTH),
    };
  }

  /** 收到 ACK 时暂存 ackTime（ACK.id 是用户消息的服务端 msg_id，与 Agent 回复无关） */
  recordAck(): void {
    if (this.pendingRound) {
      this.pendingRound.ackTime = Date.now();
    }
  }

  /**
   * 收到 message_data 时喂数据。
   *
   * 若该 msgId 尚无 tracker，则消费 pendingRound 创建一个新 tracker 并回填时间。
   *
   * 服务端 state 取值：
   *   - "delta" / "streaming": 流式中（非终态）
   *   - "final": 终态 — 正常完成
   *   - "aborted": 终态 — 中止
   *   - "error": 终态 — 错误
   *
   * @param msgId Agent 运行 ID（message_data.msg_id = runId）
   * @param content 当前全量内容
   * @param isTerminal 是否为终态（final / aborted / error）
   * @param isErrorTerminal 是否为异常终态（aborted / error）
   */
  feedMessageData(
    msgId: string,
    content: string,
    isTerminal: boolean,
    isErrorTerminal: boolean = false,
  ): void {
    let tracker = this.trackers.get(msgId);

    // 首次遇到该 msgId → 消费 pendingRound 创建 tracker
    if (!tracker) {
      // 已 finalize 过的 msgId（如 final 后迟到的 segment_end），直接忽略，保护 pendingRound
      if (this.finalizedMsgIds.has(msgId)) {
        return;
      }

      const pending = this.pendingRound;
      const sendTime = pending?.sendTime ?? Date.now();
      const ackTime = pending?.ackTime;
      const userContent = pending?.userContent;
      this.pendingRound = null;

      const timeoutTimer = setTimeout(() => {
        this.handleMessageTimeout(msgId);
      }, MESSAGE_ROUND_TIMEOUT_MS);

      tracker = {
        msgId,
        sendTime,
        ackTime,
        userContent,
        agentResponseSize: 0,
        isError: false,
        timeoutTimer,
        messageDataCount: 0,
        lastContentLength: 0,
        contentRegressionCount: 0,
        contentRegressions: [],
        maxContentLength: 0,
        finalContentLength: 0,
      };
      this.trackers.set(msgId, tracker);
    }

    tracker.messageDataCount++;
    const currentLength = content.length;

    // 首条 message_data → 记录首 token 时间
    if (tracker.messageDataCount === 1) {
      tracker.firstTokenTime = Date.now();
    }

    // 流式健康度：检测内容回退（吞字）
    if (currentLength < tracker.lastContentLength) {
      tracker.contentRegressionCount++;
      if (tracker.contentRegressions.length < 10) {
        tracker.contentRegressions.push({
          chunk: tracker.messageDataCount,
          from: tracker.lastContentLength,
          to: currentLength,
        });
      }
    }

    tracker.lastContentLength = currentLength;
    tracker.maxContentLength = Math.max(tracker.maxContentLength, currentLength);

    // 只取最后一条全量 message_data 的内容
    tracker.agentContent = content.slice(0, AGENT_CONTENT_MAX_LENGTH);
    tracker.agentResponseSize = estimateByteSize(content);

    if (isTerminal) {
      tracker.completeTime = Date.now();
      tracker.finalContentLength = currentLength;
      if (isErrorTerminal) {
        tracker.isError = true;
        tracker.errorDetail = tracker.errorDetail ?? 'Agent terminated with error state';
      }
      this.finalizeAndReport(msgId);
    }
  }

  /** 收到服务端错误时标记。优先关联指定 msgId 的 tracker，否则关联最近创建的 tracker */
  recordError(msgId: string | undefined, errorDetail: string): void {
    if (!msgId) {
      // 没有关联到具体 msgId 的错误，找最近插入的 tracker（Map 保证插入序）
      let latestMsgId: string | undefined;
      for (const key of this.trackers.keys()) {
        latestMsgId = key;
      }
      if (!latestMsgId) return;
      msgId = latestMsgId;
    }

    const tracker = this.trackers.get(msgId);
    if (!tracker) return;

    tracker.isError = true;
    tracker.errorDetail = errorDetail;
    tracker.finalContentLength = tracker.lastContentLength;
    this.finalizeAndReport(msgId);
  }

  // ---------- 内部方法 ----------

  private handleMessageTimeout(msgId: string): void {
    const tracker = this.trackers.get(msgId);
    if (!tracker) return;

    tracker.isError = true;
    tracker.errorDetail = `Message round timeout after ${MESSAGE_ROUND_TIMEOUT_MS}ms`;
    tracker.finalContentLength = tracker.lastContentLength;
    this.finalizeAndReport(msgId);
  }

  private finalizeAndReport(msgId: string): void {
    const tracker = this.trackers.get(msgId);
    if (!tracker) return;

    clearTimeout(tracker.timeoutTimer);
    this.trackers.delete(msgId);
    if (this.finalizedMsgIds.size >= 50) {
      this.finalizedMsgIds.clear();
    }
    this.finalizedMsgIds.add(msgId);

    const streamAnomalyFlag = buildStreamAnomalyFlag(tracker);
    const isAnomalous = streamAnomalyFlag !== 'normal' || tracker.isError;

    let report: MessageRoundReport = {
      msgId: tracker.msgId,
      connectionId: this.connectionId,
      conversationId: this.conversationId,
      sessionKey: this.sessionKey,
      isSuccess: !tracker.isError && !!tracker.completeTime,
      ackTimeMs: tracker.ackTime ? tracker.ackTime - tracker.sendTime : undefined,
      firstTokenTimeMs: tracker.firstTokenTime
        ? tracker.firstTokenTime - tracker.sendTime
        : undefined,
      completeTimeMs: tracker.completeTime ? tracker.completeTime - tracker.sendTime : undefined,
      agentResponseSize: tracker.agentResponseSize,
      messageDataCount: tracker.messageDataCount,
      userContent: tracker.userContent,
      agentContent: tracker.agentContent,
      errorDetail: tracker.errorDetail,
      streamAnomalyFlag,
      streamDiagnostics: buildStreamDiagnostics(tracker),
      recentMessagesSnapshot: isAnomalous ? this.recentMessages.serialize() : undefined,
    };

    report = enforcePayloadLimit(report);

    // 上报
    reportWsMessageRound(report);

    // 推入环形缓冲区
    this.recentMessages.push({
      msgId: tracker.msgId,
      role: 'assistant',
      contentPreview: (tracker.agentContent ?? '').slice(0, CONTENT_PREVIEW_LENGTH),
      timestamp: Date.now(),
      isError: tracker.isError,
    });
  }
}

// ============== 单例 ==============

let collectorInstance: WsReportCollector | null = null;

export function getWsReportCollector(): WsReportCollector {
  if (!collectorInstance) {
    collectorInstance = new WsReportCollector();
  }
  return collectorInstance;
}
